<template>
  <div class="mural-card">
    <!-- Barra de navegação -->
    <div class="flex flex-wrap items-center" style="gap: .6rem;">
      <b-button variant="outline-secondary" size="sm" @click="goToday">
        {{ $t('mural.agenda.today') }}
      </b-button>
      <span class="inline-flex" style="gap: .25rem;">
        <b-button variant="outline-secondary" size="sm" class="mural-nav-btn" @click="navigate(-1)">
          <feather-icon icon="ChevronLeftIcon" size="15" />
        </b-button>
        <b-button variant="outline-secondary" size="sm" class="mural-nav-btn" @click="navigate(1)">
          <feather-icon icon="ChevronRightIcon" size="15" />
        </b-button>
      </span>
      <span class="mural-cal-title">{{ title }}</span>
      <span class="flex-grow"></span>
      <b-button-group size="sm">
        <b-button
          v-for="option in viewOptions"
          :key="option.value"
          :variant="view === option.value ? 'primary' : 'outline-primary'"
          @click="$emit('update:view', option.value)">
          {{ option.label }}
        </b-button>
      </b-button-group>
    </div>

    <!-- Legenda por modalidade -->
    <div v-if="legend.length" class="flex flex-wrap mt-1" style="gap: .35rem 1.1rem;">
      <span v-for="item in legend" :key="item.name" class="mural-legend">
        <span class="mural-legend__dot" :style="{ background: item.color }"></span>{{ item.name }}
      </span>
    </div>

    <div v-if="!classes.length" class="text-center text-muted py-5">
      <feather-icon icon="CalendarIcon" size="38" />
      <div class="mt-1">{{ $t('mural.agenda.empty_scope') }}</div>
    </div>

    <!-- SEMANA -->
    <div v-else-if="view === 'week'" class="mural-week-scroll mt-2">
      <div class="mural-week">
        <div class="mural-week__head">
          <div></div>
          <div v-for="day in weekDays" :key="`h-${day.date}`" class="text-center pb-50 pt-25">
            <div class="mural-week__wd">{{ day.weekdayLabel }}</div>
            <span class="mural-week__num" :class="{ 'mural-week__num--today': day.isToday }">
              {{ day.dayNumber }}
            </span>
          </div>
        </div>
        <div class="mural-week__body">
          <div class="mural-week__gutter" :style="{ height: `${gridHeight}px` }">
            <div v-for="hour in hours" :key="hour" class="mural-week__hour">{{ hour }}</div>
          </div>
          <div
            v-for="day in weekDays"
            :key="`c-${day.date}`"
            class="mural-week__col"
            :class="{ 'mural-week__col--today': day.isToday }"
            :style="{ height: `${gridHeight}px` }">
            <div
              v-for="block in day.blocks"
              :key="block.key"
              class="mural-block"
              :style="block.style"
              @click="$emit('select', block.payload)">
              <div class="flex justify-between items-center" style="gap: 4px;">
                <span class="mural-block__title" :style="{ textDecoration: block.strike }">
                  {{ block.startTime }} {{ block.name }}
                </span>
                <span class="mural-block__occ">{{ block.occupancy }}</span>
              </div>
              <div class="mural-block__bar">
                <div :style="{ width: `${block.percent}%`, background: block.color }"></div>
              </div>
              <div class="mural-block__sub">{{ block.subtitle }}</div>
            </div>
            <div v-if="day.isToday" class="mural-now" :style="{ top: `${nowTop}px` }">
              <span></span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DIA -->
    <div v-else-if="view === 'day'">
      <div class="flex mt-2 mural-daystrip">
        <button
          v-for="day in weekDays"
          :key="`s-${day.date}`"
          type="button"
          class="mural-daystrip__btn"
          :class="{
            'mural-daystrip__btn--active': day.isAnchor,
            'mural-daystrip__btn--today': day.isToday && !day.isAnchor,
          }"
          @click="$emit('update:anchor', day.date)">
          <div class="mural-daystrip__wd">{{ day.weekdayLabel }}</div>
          <div class="mural-daystrip__num">{{ day.dayNumber }}</div>
        </button>
      </div>

      <div class="flex flex-column mt-2" style="gap: .7rem;">
        <div
          v-for="row in dayRows"
          :key="row.key"
          class="mural-dayrow"
          :style="{ borderLeftColor: row.color, opacity: row.elapsed ? 0.62 : 1 }"
          @click="$emit('select', row.payload)">
          <div class="mural-dayrow__time">
            <div class="font-weight-bold">{{ row.startTime }}</div>
            <div class="text-muted"><small>{{ row.endTime }}</small></div>
          </div>
          <div class="mural-dayrow__main">
            <div class="mural-dayrow__title" :style="{ textDecoration: row.strike }">{{ row.name }}</div>
            <div class="text-muted mural-dayrow__sub">{{ row.subtitle }}</div>
          </div>
          <b-badge :variant="row.badgeVariant" pill class="mr-1">{{ row.badgeLabel }}</b-badge>
          <div class="mural-dayrow__occ">
            <div class="font-weight-bold">{{ row.occupancy }}</div>
            <div class="mural-block__bar mt-25">
              <div :style="{ width: `${row.percent}%`, background: row.color }"></div>
            </div>
          </div>
          <feather-icon icon="ChevronRightIcon" size="16" class="text-muted" />
        </div>
        <div v-if="!dayRows.length" class="text-center text-muted py-5">
          <feather-icon icon="CalendarIcon" size="38" />
          <div class="mt-1">{{ $t('mural.agenda.empty_day') }}</div>
        </div>
      </div>
    </div>

    <!-- MÊS -->
    <div v-else class="mural-month mt-2">
      <div class="mural-month__grid">
        <div v-for="label in weekdayHeaders" :key="`wh-${label}`" class="mural-month__head">{{ label }}</div>
        <div
          v-for="cell in monthCells"
          :key="cell.date"
          class="mural-month__cell"
          @click="openDay(cell.date)">
          <span v-if="cell.isToday" class="mural-month__today">{{ cell.dayNumber }}</span>
          <div v-else class="mural-month__num" :class="{ 'text-muted': !cell.inMonth }">{{ cell.dayNumber }}</div>
          <div v-for="chip in cell.chips" :key="chip.key" class="mural-month__chip">
            <span class="mural-month__dot" :style="{ background: chip.color }"></span>
            <span class="mural-month__chip-text">{{ chip.text }}</span>
          </div>
          <div v-if="cell.more > 0" class="mural-month__more">
            {{ $t('mural.agenda.more', { count: cell.more }) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BBadge, BButton, BButtonGroup } from 'bootstrap-vue'
import {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  inkOf,
  isElapsed,
  isSameDay,
  longDateLabel,
  minutesOf,
  mondayOf,
  occupancyLabel,
  occupancyPercent,
  parseDate,
  slotStatus,
  tint,
  toDateString,
} from './muralAgenda'

const HOUR_HEIGHT = 56

/**
 * A grade da semana/dia/mês do Mural.
 *
 * Recebe a janela já expandida pelo backend (`days[].slots[]`) e só posiciona.
 * A faixa de horas não é fixa: é calculada a partir das aulas da janela, senão
 * uma turma das 05:00 ou das 22:00 ficaria fora da grade sem qualquer aviso.
 */
export default {
  name: 'MuralAgenda',
  components: { BBadge, BButton, BButtonGroup },
  props: {
    days: { type: Array, default: () => [] },
    classes: { type: Array, default: () => [] },
    view: { type: String, default: 'week' },
    anchor: { type: String, required: true },
  },
  computed: {
    viewOptions() {
      return [
        { value: 'day', label: this.$t('mural.agenda.day') },
        { value: 'week', label: this.$t('mural.agenda.week') },
        { value: 'month', label: this.$t('mural.agenda.month') },
      ]
    },
    weekdayHeaders() {
      return WEEKDAY_SHORT
    },
    classById() {
      return this.classes.reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {})
    },
    /** Só as modalidades que realmente aparecem na janela. */
    legend() {
      const seen = new Map()
      this.classes.forEach(item => {
        if (item.modality_name && !seen.has(item.modality_name)) {
          seen.set(item.modality_name, { name: item.modality_name, color: item.color })
        }
      })
      return Array.from(seen.values())
    },
    anchorDate() {
      return parseDate(this.anchor)
    },
    dayMap() {
      return this.days.reduce((acc, day) => {
        acc[day.date] = day
        return acc
      }, {})
    },
    /**
     * Primeira e última hora com aula na janela, com folga de uma hora de cada
     * lado e um mínimo de 06:00–21:00 para a grade não parecer amputada num dia
     * de poucas aulas.
     */
    hourBounds() {
      let start = 6 * 60
      let end = 21 * 60

      this.days.forEach(day => {
        (day.slots || []).forEach(slot => {
          start = Math.min(start, minutesOf(slot.startTime) - 60)
          end = Math.max(end, minutesOf(slot.endTime) + 60)
        })
      })

      return {
        start: Math.max(0, Math.floor(start / 60) * 60),
        end: Math.min(24 * 60, Math.ceil(end / 60) * 60),
      }
    },
    hours() {
      const list = []
      for (let m = this.hourBounds.start; m < this.hourBounds.end; m += 60) {
        list.push(`${String(Math.floor(m / 60)).padStart(2, '0')}:00`)
      }
      return list
    },
    gridHeight() {
      return this.hours.length * HOUR_HEIGHT
    },
    nowTop() {
      const now = new Date()
      const minutes = now.getHours() * 60 + now.getMinutes()
      return ((minutes - this.hourBounds.start) / 60) * HOUR_HEIGHT
    },
    weekDays() {
      const monday = mondayOf(this.anchorDate)
      const today = new Date()

      return WEEKDAY_SHORT.map((label, index) => {
        const date = new Date(monday)
        date.setDate(date.getDate() + index)
        const key = toDateString(date)
        const day = this.dayMap[key]

        return {
          date: key,
          weekdayLabel: label,
          dayNumber: date.getDate(),
          isToday: isSameDay(date, today),
          isAnchor: isSameDay(date, this.anchorDate),
          blocks: this.blocksOf(day),
        }
      })
    },
    dayRows() {
      const day = this.dayMap[this.anchor]
      if (!day) return []

      return (day.slots || []).map(slot => {
        const lesson = this.classById[slot.lessonId] || {}
        const status = slotStatus(slot)
        const badges = {
          cancelled: 'light-danger',
          blocked: 'light-dark',
          full: 'light-warning',
          scheduled: 'light-primary',
        }

        return {
          key: this.slotKey(day.date, slot),
          color: this.colorFor(slot, lesson),
          name: lesson.name || '',
          subtitle: this.subtitleOf(lesson),
          startTime: slot.startTime,
          endTime: slot.endTime,
          occupancy: occupancyLabel(slot),
          percent: occupancyPercent(slot),
          strike: status === 'cancelled' ? 'line-through' : 'none',
          elapsed: isElapsed(day.date, slot.startTime),
          badgeVariant: badges[status],
          badgeLabel: this.$t(`mural.status.${status}`),
          payload: { date: day.date, slot, lesson },
        }
      })
    },
    /**
     * A grade do mês tem sempre 6 semanas, para não pular de altura ao trocar de
     * mês. As células de fora do mês corrente entram esmaecidas.
     */
    monthCells() {
      const first = new Date(this.anchorDate.getFullYear(), this.anchorDate.getMonth(), 1)
      const start = mondayOf(first)
      const today = new Date()
      const cells = []

      for (let i = 0; i < 42; i++) {
        const date = new Date(start)
        date.setDate(start.getDate() + i)
        const key = toDateString(date)
        const slots = (this.dayMap[key] || {}).slots || []
        const chips = slots.slice(0, 3).map(slot => {
          const lesson = this.classById[slot.lessonId] || {}
          return {
            key: this.slotKey(key, slot),
            color: this.colorFor(slot, lesson),
            text: `${slot.startTime} ${lesson.name || ''}`,
          }
        })

        cells.push({
          date: key,
          dayNumber: date.getDate(),
          inMonth: date.getMonth() === this.anchorDate.getMonth(),
          isToday: isSameDay(date, today),
          chips,
          more: Math.max(0, slots.length - chips.length),
        })
      }

      return cells
    },
    title() {
      if (this.view === 'day') {
        return longDateLabel(this.anchorDate)
      }
      if (this.view === 'month') {
        const name = MONTH_NAMES[this.anchorDate.getMonth()]
        return `${name.charAt(0).toUpperCase()}${name.slice(1)} de ${this.anchorDate.getFullYear()}`
      }

      const monday = mondayOf(this.anchorDate)
      const sunday = new Date(monday)
      sunday.setDate(sunday.getDate() + 6)

      if (monday.getMonth() === sunday.getMonth()) {
        return `${monday.getDate()} – ${sunday.getDate()} de ${MONTH_NAMES[sunday.getMonth()]} de ${sunday.getFullYear()}`
      }
      return `${monday.getDate()} de ${MONTH_NAMES[monday.getMonth()].slice(0, 3)} – `
        + `${sunday.getDate()} de ${MONTH_NAMES[sunday.getMonth()].slice(0, 3)} de ${sunday.getFullYear()}`
    },
  },
  methods: {
    slotKey(date, slot) {
      return `${slot.lessonId}|${date}|${slot.startTime}|${slot.endTime}`
    },
    /** Cancelada e bloqueada perdem a cor da modalidade: a situação vem antes. */
    colorFor(slot, lesson) {
      const status = slotStatus(slot)
      if (status === 'cancelled') return '#EA5455'
      if (status === 'blocked') return '#4B4B4B'
      return lesson.color || '#8BB2DB'
    },
    subtitleOf(lesson) {
      const teacher = (lesson.teachers || [])[0]
      const parts = []
      if (teacher) parts.push(teacher.name)
      if (lesson.rental_space) parts.push(lesson.rental_space)
      return parts.join(' · ') || lesson.modality_name || ''
    },
    blocksOf(day) {
      if (!day) return []

      return (day.slots || []).map(slot => {
        const lesson = this.classById[slot.lessonId] || {}
        const color = this.colorFor(slot, lesson)
        const top = ((minutesOf(slot.startTime) - this.hourBounds.start) / 60) * HOUR_HEIGHT
        const height = Math.max(
          28,
          ((minutesOf(slot.endTime) - minutesOf(slot.startTime)) / 60) * HOUR_HEIGHT - 4,
        )

        return {
          key: this.slotKey(day.date, slot),
          startTime: slot.startTime,
          name: lesson.name || '',
          subtitle: this.subtitleOf(lesson),
          occupancy: occupancyLabel(slot),
          percent: occupancyPercent(slot),
          color,
          strike: slotStatus(slot) === 'cancelled' ? 'line-through' : 'none',
          style: {
            top: `${top + 1}px`,
            height: `${height}px`,
            background: tint(color),
            borderLeftColor: color,
            color: inkOf(color),
            opacity: isElapsed(day.date, slot.startTime) ? 0.6 : 1,
          },
          payload: { date: day.date, slot, lesson },
        }
      })
    },
    goToday() {
      this.$emit('update:anchor', toDateString(new Date()))
    },
    navigate(direction) {
      const date = parseDate(this.anchor)
      if (this.view === 'day') date.setDate(date.getDate() + direction)
      else if (this.view === 'week') date.setDate(date.getDate() + direction * 7)
      else date.setMonth(date.getMonth() + direction)
      this.$emit('update:anchor', toDateString(date))
    },
    openDay(date) {
      this.$emit('update:anchor', date)
      this.$emit('update:view', 'day')
    },
  },
}
</script>

<style lang="scss" scoped>
.mural-card {
  background: var(--surface-card);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: 1.25rem;
}

.mural-nav-btn {
  width: 30px;
  padding-left: 0;
  padding-right: 0;
}

.mural-cal-title {
  font-size: 1.07rem;
  font-weight: var(--fw-semibold);
  color: var(--text-heading);
  white-space: nowrap;
}

.mural-legend {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;

  &__dot {
    width: 9px;
    height: 9px;
    border-radius: var(--radius-pill);
  }
}

/* ---- Semana ---- */
.mural-week-scroll {
  overflow-x: auto;
}

.mural-week {
  min-width: 660px;

  &__head,
  &__body {
    display: grid;
    grid-template-columns: 46px repeat(7, 1fr);
  }

  &__body {
    border-top: 1px solid var(--border-color);
  }

  &__wd {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  &__num {
    display: inline-flex;
    width: 26px;
    height: 26px;
    border-radius: var(--radius-pill);
    align-items: center;
    justify-content: center;
    font-size: 0.95rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);

    &--today {
      background: var(--primary);
      color: #fff;
    }
  }

  &__gutter {
    position: relative;
  }

  &__hour {
    height: 56px;
    font-size: 0.7rem;
    color: var(--text-muted);
    text-align: right;
    padding-right: 6px;
    transform: translateY(-6px);
  }

  &__col {
    position: relative;
    border-left: 1px solid var(--border-color);
    background-image: repeating-linear-gradient(
      to bottom,
      var(--border-color) 0,
      var(--border-color) 1px,
      transparent 1px,
      transparent 56px
    );

    &--today {
      background-color: var(--tint-primary);
    }
  }
}

.mural-block {
  position: absolute;
  left: 3px;
  right: 3px;
  border-left: 3px solid;
  border-radius: 4px;
  padding: 3px 6px;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;

  &:hover {
    box-shadow: var(--shadow-input-focus);
  }

  &__title {
    font-size: 0.72rem;
    font-weight: var(--fw-semibold);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__occ {
    font-size: 0.68rem;
    font-weight: 700;
    white-space: nowrap;
  }

  &__bar {
    height: 3px;
    border-radius: var(--radius-pill);
    background: rgba(0, 0, 0, 0.08);
    margin: 3px 0;

    > div {
      height: 100%;
      border-radius: var(--radius-pill);
    }
  }

  &__sub {
    font-size: 0.68rem;
    opacity: 0.85;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.mural-now {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--danger);
  z-index: 2;

  span {
    position: absolute;
    left: -4px;
    top: -3px;
    width: 8px;
    height: 8px;
    border-radius: var(--radius-pill);
    background: var(--danger);
  }
}

/* ---- Dia ---- */
.mural-daystrip {
  gap: 0.4rem;
  overflow-x: auto;
  padding-bottom: 0.35rem;

  &__btn {
    flex: 1;
    min-width: 46px;
    padding: 0.45rem 0.3rem;
    border: 1px solid var(--input-border);
    background: #fff;
    color: var(--text-heading);
    border-radius: 0.5rem;
    cursor: pointer;
    font-family: var(--font-ui);
    text-align: center;

    &--active {
      background: var(--primary);
      border-color: var(--primary);
      color: #fff;
    }

    &--today {
      background: var(--tint-primary);
      border-color: var(--primary);
    }
  }

  &__wd {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.8;
  }

  &__num {
    font-size: 0.95rem;
    font-weight: var(--fw-semibold);
  }
}

.mural-dayrow {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  border: 1px solid var(--border-color);
  border-left: 4px solid;
  border-radius: 0.5rem;
  padding: 0.75rem 0.9rem;
  cursor: pointer;
  background: #fff;

  &:hover {
    background: var(--surface-hover);
  }

  &__time {
    flex: none;
    width: 46px;
    text-align: center;
  }

  &__main {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: var(--fw-semibold);
    color: var(--text-heading);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__sub {
    font-size: 0.78rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__occ {
    flex: none;
    width: 64px;
    text-align: right;
    font-size: 0.82rem;
  }
}

/* ---- Mês ---- */
.mural-month {
  border: 1px solid var(--border-color);
  border-radius: var(--radius);
  overflow: hidden;

  &__grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    background: var(--border-color);
  }

  &__head {
    background: var(--surface-sunken);
    padding: 0.4rem;
    text-align: center;
    font-size: 0.72rem;
    font-weight: var(--fw-semibold);
    text-transform: uppercase;
    color: var(--text-heading);
  }

  &__cell {
    background: #fff;
    min-height: 96px;
    padding: 3px 4px;
    cursor: pointer;
    overflow: hidden;

    &:hover {
      background: var(--surface-hover);
    }
  }

  &__today {
    display: inline-flex;
    width: 20px;
    height: 20px;
    border-radius: var(--radius-pill);
    background: var(--primary);
    color: #fff;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    margin: 1px 0 2px 1px;
  }

  &__num {
    font-size: 0.78rem;
    padding: 2px 1px;
    color: var(--text-body);
  }

  &__chip {
    display: flex;
    align-items: center;
    gap: 3px;
    margin-top: 2px;
    overflow: hidden;
  }

  &__dot {
    width: 6px;
    height: 6px;
    flex: none;
    border-radius: var(--radius-pill);
  }

  &__chip-text {
    font-size: 0.68rem;
    color: var(--text-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__more {
    font-size: 0.65rem;
    color: var(--text-muted);
    margin-top: 2px;
  }
}

@media (max-width: 767px) {
  .mural-month__cell {
    min-height: 58px;
  }

  .mural-month__chip-text {
    display: none;
  }
}
</style>
